




























































































































































































import axios from 'axios';
import debounce from 'lodash/debounce';
import CoTippy from '@/components/Atoms/co-tippy/CoTippy.vue';
import CoIcon from '@/components/Atoms/co-icon/CoIcon.vue';
import CoText from '../../Atoms/co-text/CoText.vue';
import CoCheckbox from '../../Atoms/co-checkbox/CoCheckbox.vue';
import CoInput from '../co-input/CoInput.vue';
import CoThumbnail from '../co-thumbnail/CoThumbnail.vue';
import CoRoundButton from '@/components/Atoms/co-round-button/CoRoundButton.vue';

interface Item {
    Name: string;
    Value: string | number | boolean | null | undefined | object;
    ShowThumbnail?: boolean; // Whether to show thumbnail
    ThumbnailImageURL?: string; // Optional image URL
    SecondaryThumbnailImageURL?: string; // Optional secondary image URL displayed as small icon
    RoundThumbnail?: boolean; // Use round thumbnail style
    Disabled?: boolean;
    BorderBelow?: boolean;
    AlwaysSelected?: boolean; // Whether this item is always selected and cannot be deselected
}

export default {
    name: 'CoMultiSelect',
    components: { CoText, CoCheckbox, CoInput, CoThumbnail, CoTippy, CoIcon, CoRoundButton },
    props: {
        value: {
            type: Array,
            default: () => [] as Item[],
        },
        items: {
            type: Array,
            default: () => [] as Item[],
        },
        placeholder: {
            type: String,
            default: 'Select',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        selectFirstItem: {
            type: Boolean,
            default: false,
        },
        fitContent: {
            type: Boolean,
            default: false,
        },
        maxWidth: {
            type: [String, Number],
            default: null,
        },
        plusAsSeparator: {
            type: Boolean,
            default: false,
        },
        showChevronDown: {
            type: Boolean,
            default: false,
        },
        searchable: {
            type: Boolean,
            default: true,
        },
        searchFn: {
            type: Function,
            default: null, // (query, items) => filteredItems
        },
        searchPlaceholder: {
            type: String,
            default: 'Search...',
        },
        noOptionsLabel: {
            type: String,
            default: '',
        },
        showSelectAll: {
            type: Boolean,
            default: true,
        },
        staticItems: {
            type: Array,
            default: () => [] as Item[],
        },
        maxSelected: {
            type: Number,
            default: null,
        },
        tippyOptions: {
            type: Object,
            default: () => ({}),
        },
        hideAllOnShow: {
            type: Boolean,
            default: true,
        },
        variant: {
            type: String,
            default: 'default',
            validator(value: string) {
                return ['default', 'lightgrey', 'border'].includes(value);
            },
        },
    },
    data() {
        return {
            selectOpen: false,
            searchQuery: '',
            searchResults: [] as Item[],
            searchLoading: false,
            selectedItems: ([] as Item[]).concat(this.value || []),
            source: null,
            isMobileDevice: navigator && navigator.userAgent && navigator.userAgent.match(/iPad|iPhone|Android/i),
            triggerId: `co-multi-select-${Math.random().toString(36).slice(2)}`,
            tippyHiddenDebounce: null as null | ReturnType<typeof debounce>,
            lastToggleTs: 0,
        };
    },
    computed: {
        classes() {
            return {
                [this.variant]: true,
                expanded: this.selectOpen,
                disabled: this.disabled,
                'pr-0': this.showChevronDown,
                'fit-content-collapsed': this.useCollapsedSlot,
            };
        },
        containerStyles() {
            const styles: Record<string, string> = {};
            const expandedWidth = this.resolvedExpandedWidth;

            if (this.useCollapsedSlot && !this.selectOpen) {
                // Let custom collapsed content define the trigger width before the dropdown opens.
                styles.width = 'fit-content';
                styles.maxWidth = 'none';
            } else {
                styles.width = this.fitContent ? 'auto' : expandedWidth;
                if (this.fitContent) {
                    styles.maxWidth = expandedWidth;
                } else {
                    styles.maxWidth = expandedWidth;
                }
            }

            return styles;
        },
        useCollapsedSlot(): boolean {
            return Boolean(this.$slots.collapsed);
        },
        useTippy(): boolean {
            // return !this.isMobileDevice;
            return true;
        },
        resolvedExpandedWidth(): string {
            if (!this.maxWidth && this.maxWidth !== 0) return '300px';
            return typeof this.maxWidth === 'number' ? `${this.maxWidth}px` : `${this.maxWidth}`;
        },
        selectedSeparator(): string {
            return this.plusAsSeparator ? '+' : ',';
        },
        resolvedTippyOptions(): Record<string, unknown> {
            const incomingOptions = (this.tippyOptions || {}) as Record<string, any>;
            const { popperOptions = {}, trigger: _ignoredTrigger, ...rest } = incomingOptions;
            const defaultPopperOptions = {
                modifiers: [
                    {
                        name: 'sameWidth',
                        enabled: true,
                        phase: 'beforeWrite',
                        requires: ['computeStyles'],
                        fn({ state }: any) {
                            state.styles.popper.width = `${state.rects.reference.width}px`;
                        },
                        effect({ state }: any) {
                            const reference = state.elements.reference as HTMLElement;
                            const popper = state.elements.popper as HTMLElement;
                            popper.style.width = `${reference.offsetWidth}px`;
                        },
                    },
                ],
            };
            const mergedPopperOptions = {
                ...defaultPopperOptions,
                ...popperOptions,
                modifiers: [...(defaultPopperOptions.modifiers || []), ...(popperOptions.modifiers || [])],
            };

            return {
                trigger: 'manual',
                interactive: true,
                inlinePositioning: true,
                delay: 0,
                arrow: false,
                zIndex: 9000, // make sure dropdowns are below tooltips (default is 9999)
                offset: [0, 4], // vertical offset 4px
                placement: 'bottom-start',
                maxWidth: 'none',
                ...rest,
                popperOptions: mergedPopperOptions,
            };
        },
        filteredItems(): Item[] {
            const selectedItems = this.selectedItems || [];
            if (this.searchable && this.searchFn) {
                const baseItems = this.searchQuery ? this.searchResults : this.items || [];
                const combined = this.mergeItems([...this.resolvedStaticItems, ...baseItems, ...selectedItems]);
                return this.withMaxSelectionDisabled(combined, selectedItems);
            }
            const q = this.searchQuery?.toLowerCase() || '';
            const itemsOutput = this.items?.filter((item: Item) => (item.Name || '').toLowerCase().includes(q));

            return this.withMaxSelectionDisabled(
                this.mergeItems([...this.resolvedStaticItems, ...(itemsOutput || [])]),
                selectedItems
            );
        },
        allSelected(): boolean {
            const filtered = this.filteredItems || [];
            if (filtered.length === 0) return false;
            const enabled = filtered.filter((item) => !item.Disabled);
            return enabled.length > 0 && enabled.every((item) => this.isSelected(item));
        },
        allDisabled(): boolean {
            return this.filteredItems?.every((item) => item.Disabled);
        },
        visibleSelected(): Item[] {
            // Show 1-2 selected items if fit, rest as +n
            const maxLabels = 2;
            const staticVals = new Set(this.resolvedStaticItems.map((s) => s.Value));
            const sorted = [...this.selectedItems].sort((a, b) => {
                const aStatic = staticVals.has(a.Value);
                const bStatic = staticVals.has(b.Value);
                if (aStatic && !bStatic) return -1;
                if (!aStatic && bStatic) return 1;
                return 0;
            });
            return sorted.slice(0, maxLabels);
        },
        resolvedStaticItems(): Item[] {
            return (this.staticItems || []).map((item: Item) => ({
                Disabled: false,
                ...item,
            }));
        },
    },
    watch: {
        value(newVal) {
            this.selectedItems = ([] as Item[]).concat(newVal || []);
        },
        items(newItems) {
            // Remove deselected items if not in new list
            const keepable = [...(newItems || []), ...this.resolvedStaticItems];
            this.selectedItems = this.selectedItems.filter((sel) => keepable.some((i) => i.Value === sel.Value));
        },
        searchQuery(val) {
            if (!this.searchable || !this.searchFn) return;
            this.search(val);
        },
        selectOpen(isOpen: boolean) {
            if (!this.useTippy) return;
            if (isOpen) {
                this.$nextTick(() => {
                    this.$refs.selectTippy?.show();
                });
            } else {
                this.$refs.selectTippy?.hide();
            }
        },
        disabled(newValue) {
            if (newValue) {
                this.selectOpen = false;
            }

            if (!this.$refs.selectTippy) return;

            if (newValue) {
                this.$refs.selectTippy?.hide();
                this.$refs.selectTippy?.tippyInstance?.disable();
            } else {
                this.$refs.selectTippy?.tippyInstance?.enable();
            }
        },
    },
    created() {
        // Debounce tippy hidden handling to avoid closing just-opened dropdowns when events overlap
        this.tippyHiddenDebounce = debounce(() => {
            // Ignore hidden events that fire right after a user-triggered toggle
            if (Date.now() - this.lastToggleTs < 120) return;
            if (!this.selectOpen) return;
            this.selectOpen = false;
            this.lastToggleTs = Date.now();
        }, 50);
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },
    destroyed() {
        document.removeEventListener('click', this.handleClickOutside);
        this.tippyHiddenDebounce?.cancel?.();
    },
    methods: {
        async search(input: string) {
            this.searchLoading = true;
            if (!this.searchable || !this.searchFn) {
                this.searchResults = [];
            }

            // Cancel previous search if exists
            if (this.source) {
                this.source.cancel('Operation canceled by the user.');
            }
            const query = (input || '').toLowerCase();

            try {
                this.source = axios.CancelToken.source();
                const results = await this.searchFn(query, this.source.token);
                this.searchResults = results || [];
                this.searchLoading = false;
            } catch (error) {
                console.error('Search function error:', error);
                if (axios.isCancel(error)) {
                    console.warn('Search request canceled:', error.message);
                } else {
                    this.$emit('error', error);
                }
                this.searchResults = [];
                this.searchLoading = false;
            }
        },
        toggleDropdown() {
            if (this.disabled) return;
            this.tippyHiddenDebounce?.cancel?.();
            if (Date.now() - this.lastToggleTs < 120) return;
            this.selectOpen = !this.selectOpen;
            this.lastToggleTs = Date.now();
            if (this.selectOpen) {
                this.searchQuery = '';
                this.searchResults = [];
                this.searchLoading = false;
            }
        },
        isSelected(item: Item) {
            return this.selectedItems.some((sel) => sel.Value === item.Value);
        },
        toggleItem(item: Item) {
            if (item.Disabled) return;
            let arr = this.selectedItems.slice();
            if (this.isSelected(item)) {
                arr = arr.filter((sel) => sel.Value !== item.Value);
            } else {
                if (this.maxSelected && arr.length >= this.maxSelected) {
                    this.$emit('limit', { max: this.maxSelected, attempted: item });
                    return;
                }
                arr.push(item);
            }
            this.updateSelection(arr);
        },
        toggleSelectAll(val) {
            if (this.allDisabled) return;
            if (this.allSelected) {
                // Deselect all enabled
                const disabledVals = this.selectedItems?.filter((i) => i.AlwaysSelected || i.Disabled) || [];
                this.updateSelection(disabledVals);
            } else {
                // Select all enabled
                const enabled = this.filteredItems?.filter((i) => !i.Disabled);
                const disabled = this.selectedItems?.filter((i) => i.Disabled);
                let next = [...(enabled || []), ...(disabled || [])];
                if (this.maxSelected && this.maxSelected > 0) {
                    const allowedEnabled = Math.max(this.maxSelected - (disabled?.length || 0), 0);
                    next = [...(disabled || []), ...(enabled || []).slice(0, allowedEnabled)];
                }
                this.updateSelection(this.mergeItems(next));
            }
        },
        onSearch(val: string) {
            this.searchQuery = val;
        },
        handleClickOutside(event: MouseEvent) {
            const target = event.target as Node | null;
            if (!target) return;
            const popperEl = this.$refs.selectTippy?.tippyInstance?.popper;
            const clickedInsideComponent = this.$el.contains(target);
            const clickedInsidePopper = popperEl ? popperEl.contains(target) : false;

            if ((clickedInsideComponent || clickedInsidePopper) && this.selectOpen) {
                return;
            }

            if (this.selectOpen) {
                this.selectOpen = false;
                if (this.useTippy) {
                    this.$refs.selectTippy?.hide();
                }
            }
        },
        handleTippyHidden() {
            this.tippyHiddenDebounce?.();
        },
        mergeItems(items: Item[] = []): Item[] {
            const seen = new Set();
            const output: Item[] = [];
            items.forEach((item) => {
                if (!item) return;
                const key =
                    item.Value !== undefined && item.Value !== null
                        ? String(item.Value)
                        : item.Name || Math.random().toString(36).slice(2);
                if (seen.has(key)) return;
                seen.add(key);
                output.push({
                    Disabled: false,
                    ...item,
                });
            });
            return output;
        },
        withMaxSelectionDisabled(items: Item[] = [], selectedItems: Item[] = []): Item[] {
            const maxReached = this.maxSelected && selectedItems.length >= this.maxSelected;
            if (!maxReached) return items;
            const selectedValues = new Set(
                (selectedItems || []).map((sel) =>
                    sel.Value !== undefined && sel.Value !== null ? String(sel.Value) : sel.Name
                )
            );
            return items.map((item) => {
                const key = item.Value !== undefined && item.Value !== null ? String(item.Value) : item.Name;
                const isSelected = selectedValues.has(key);
                return {
                    ...item,
                    Disabled: item.Disabled || !isSelected,
                };
            });
        },
        updateSelection(arr: Item[] = []) {
            this.selectedItems = arr;
            this.$emit('input', arr);
            this.$emit('select', arr);
        },
    },
};
